import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { KeyRound, ScanLine, ShieldCheck, Smartphone } from "lucide-react";
import { supabase } from "@/lib/supabase";
import { withTimeout } from "@/lib/timeout";
import { useAuthStore } from "@/store/auth";

/**
 * Mandatory MFA-enrollment page. Hit when a signed-in user lands on the app
 * without a verified TOTP factor — every new invite + every existing
 * MFA-less user is routed here by AppShell before they see any other
 * surface. There is no skip button; security is policy, not preference.
 *
 * On successful verify, we re-hydrate the auth store so hasMfa flips to
 * true and the AppShell guard stops redirecting. Then we navigate to
 * /dashboard like a normal post-login landing.
 *
 * NOT mounted under AdminRoute — every signed-in user, regardless of role,
 * must enroll. The route IS gated by RequireAuth so an unauthenticated user
 * can't open it directly.
 */
export function OnboardingMfaPage() {
  const navigate = useNavigate();
  const user = useAuthStore((s) => s.user);
  const rehydrateUser = useAuthStore((s) => s.rehydrateUser);

  // Three-state machine for the enrollment flow:
  //   "loading"  → calling mfa.enroll to mint a fresh factor
  //   "verify"   → QR shown, waiting on the 6-digit code
  //   "done"     → verified, about to navigate away
  const [phase, setPhase] = useState<"loading" | "verify" | "done">("loading");
  const [qrCodeSvg, setQrCodeSvg] = useState<string | null>(null);
  // Manual-entry alternative for users enrolling on the SAME device they're
  // installing the authenticator on (typical mobile flow — you can't scan a
  // QR with the camera of the phone showing it). Supabase returns both the
  // shared secret + the full otpauth:// URI on mfa.enroll.
  const [secret, setSecret] = useState<string | null>(null);
  const [otpauthUri, setOtpauthUri] = useState<string | null>(null);
  const [showManual, setShowManual] = useState(false);
  const [factorId, setFactorId] = useState<string | null>(null);
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Kick off enrollment on first mount. We don't auto-enroll if the user
  // somehow already has a verified factor (e.g. their hasMfa flag was just
  // out of sync) — in that case we just navigate away.
  useEffect(() => {
    let cancelled = false;
    async function startEnroll() {
      // Defensive: if hasMfa is true by the time this page mounts, just
      // route home. Can happen if a parallel tab enrolled the user.
      if (user?.hasMfa) {
        navigate("/dashboard", { replace: true });
        return;
      }
      try {
        // Second defensive check: query factors directly. Catches the
        // edge case where hydrateUser's listFactors call failed silently
        // (network drop on app boot) → hasMfa stayed false → user routed
        // here even though they actually have a verified factor. Without
        // this guard we'd happily enroll a SECOND factor on top of the
        // existing one. Cheap server call, self-heals the store too.
        const existing = await supabase.auth.mfa.listFactors();
        if (cancelled) return;
        const verifiedExisting = (existing.data?.totp ?? []).filter((f) => f.status === "verified");
        if (verifiedExisting.length > 0) {
          // User actually has MFA — store's hasMfa flag was stale. Rehydrate
          // so the AppShell guard stops redirecting + navigate home.
          await useAuthStore.getState().rehydrateUser();
          if (!cancelled) navigate("/dashboard", { replace: true });
          return;
        }

        // Clean up any unverified factor leftover from a prior aborted
        // enrollment — Supabase doesn't auto-collect them and they'd
        // accumulate on the user.
        const stale = (existing.data?.totp ?? []).filter((f) => f.status !== "verified");
        await Promise.all(stale.map((f) => supabase.auth.mfa.unenroll({ factorId: f.id }).catch(() => null)));

        const { data, error: enrollErr } = await supabase.auth.mfa.enroll({
          factorType: "totp",
          issuer: "Routes Made Simple",
          friendlyName: "Google Authenticator",
        });
        if (cancelled) return;
        if (enrollErr) {
          setError(enrollErr.message);
          setPhase("verify"); // show the error UI instead of an infinite loading state
          return;
        }
        setFactorId(data.id);
        setQrCodeSvg(data.totp.qr_code);
        // Capture the manual-entry fallbacks. Supabase returns both in
        // data.totp.{secret, uri}. The secret is what Google Authenticator's
        // "Enter a setup key" flow accepts; the otpauth:// URI works as a
        // clickable link in some mobile authenticator apps that register
        // it as a URI handler.
        setSecret(data.totp.secret ?? null);
        setOtpauthUri(data.totp.uri ?? null);
        setPhase("verify");
      } catch (e) {
        if (cancelled) return;
        setError(e instanceof Error ? e.message : "Couldn't start enrollment");
        setPhase("verify");
      }
    }
    void startEnroll();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function verify() {
    const trimmed = code.trim();
    if (!factorId) {
      setError("Enrollment hasn't started yet. Refresh and try again.");
      return;
    }
    if (!/^\d{6}$/.test(trimmed)) {
      setError("Code must be exactly 6 digits.");
      return;
    }
    setError(null);
    setBusy(true);
    try {
      const { data: challenge, error: challengeErr } = await withTimeout(
        supabase.auth.mfa.challenge({ factorId }),
        15_000,
        "MFA challenge",
      );
      if (challengeErr || !challenge?.id) {
        setError(challengeErr?.message ?? "Couldn't start the challenge. Try again.");
        return;
      }
      const { error: verifyErr } = await withTimeout(
        supabase.auth.mfa.verify({
          factorId,
          challengeId: challenge.id,
          code: trimmed,
        }),
        15_000,
        "MFA verify",
      );
      if (verifyErr) {
        const msg = verifyErr.message;
        setError(
          msg.toLowerCase().includes("invalid")
            ? "That code didn't match. Codes change every 30 seconds — open the app and use the fresh one."
            : msg,
        );
        return;
      }
      setPhase("done");
      // Re-hydrate user so hasMfa flips true; the AppShell guard then lets
      // them past. Bound the rehydrate too — a stalled app_users select
      // shouldn't trap the button at "Verifying…" since the verify itself
      // already succeeded server-side. Worst case we navigate anyway and
      // AppShell will re-hydrate on its own.
      try {
        await withTimeout(rehydrateUser(), 10_000, "Refreshing session");
      } catch {
        // swallow — navigation below will trigger another hydrate cycle
      }
      navigate("/dashboard", { replace: true });
    } catch (e) {
      setError(e instanceof Error ? e.message : "Couldn't verify the code");
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-slate-50 px-4 py-10">
      <div className="w-full max-w-lg rounded-xl bg-white p-8 shadow-sm ring-1 ring-slate-200">
        <div className="mb-6 flex items-center gap-3">
          <img src="/favicon.svg" alt="" className="h-10 w-10 rounded-lg shadow-sm" />
          <div className="leading-tight">
            <h1 className="text-sm font-bold tracking-tight text-slate-900">Routes Made</h1>
            <p className="text-sm font-extrabold tracking-[0.2em] text-brand-600">SIMPLE</p>
          </div>
        </div>

        <h2 className="text-xl font-semibold text-slate-900">Set up two-factor sign-in</h2>
        <p className="mt-1 text-sm text-slate-600">
          Two-factor is required for every account on the team. It takes about 60 seconds — once it's set up
          you'll sign in with your password + a 6-digit code from an app on your phone.
        </p>

        <div className="mt-5 rounded-md border border-slate-200 bg-slate-50 p-4 text-xs text-slate-700">
          <p className="font-semibold text-slate-900">What you'll need:</p>
          <ol className="mt-2 space-y-2">
            <li className="flex gap-2">
              <Smartphone className="mt-0.5 h-3.5 w-3.5 shrink-0 text-brand-600" />
              <span>
                <strong>Google Authenticator on your phone.</strong> Use your personal or work phone — whichever you
                carry every day. Free on the
                {" "}<a href="https://apps.apple.com/us/app/google-authenticator/id388497605" target="_blank" rel="noreferrer" className="text-brand-700 underline">App Store</a>
                {" "}or
                {" "}<a href="https://play.google.com/store/apps/details?id=com.google.android.apps.authenticator2" target="_blank" rel="noreferrer" className="text-brand-700 underline">Google Play</a>.
              </span>
            </li>
            <li className="flex gap-2">
              <ScanLine className="mt-0.5 h-3.5 w-3.5 shrink-0 text-brand-600" />
              <span>Open the app, tap <strong>+</strong>, choose <strong>Scan a QR code</strong>, point the camera at the image below.</span>
            </li>
            <li className="flex gap-2">
              <KeyRound className="mt-0.5 h-3.5 w-3.5 shrink-0 text-brand-600" />
              <span>Type the 6-digit code the app shows you. It changes every 30 seconds.</span>
            </li>
          </ol>
        </div>

        {phase === "loading" && (
          <div className="mt-6 flex items-center gap-2 text-sm text-slate-500">
            <span className="inline-block h-4 w-4 animate-spin rounded-full border-2 border-brand-600 border-t-transparent" />
            Preparing your QR code…
          </div>
        )}

        {phase === "verify" && qrCodeSvg && (
          <div className="mt-6 rounded-md border border-slate-200 bg-white p-4">
            <p className="text-xs font-semibold text-slate-900">Step 1 — Scan this QR code</p>
            <div className="mt-2 inline-block rounded border border-slate-200 bg-white p-2">
              <img src={qrCodeSvg} alt="Two-factor setup QR code" className="block h-48 w-48" />
            </div>

            {/* Manual entry fallback — critical on mobile, where the user
                is enrolling on the SAME device that's running the
                authenticator app and therefore can't aim a camera at this
                QR. Two paths offered: a clickable otpauth:// link that
                some authenticator apps accept as a handler, plus the raw
                secret for the "Enter a setup key" manual flow. */}
            {(secret || otpauthUri) && (
              <div className="mt-3">
                {!showManual ? (
                  <button
                    type="button"
                    onClick={() => setShowManual(true)}
                    className="text-[11px] font-medium text-brand-700 hover:underline"
                  >
                    Can't scan? Enter the setup key manually instead →
                  </button>
                ) : (
                  <div className="rounded-md border border-slate-200 bg-white p-3 text-[11px]">
                    {otpauthUri && (
                      <p className="mb-2">
                        <a href={otpauthUri} className="font-medium text-brand-700 underline">
                          Open in your authenticator app
                        </a>
                        {" "}<span className="text-slate-500">(works on mobile when an app is installed)</span>
                      </p>
                    )}
                    {secret && (
                      <>
                        <p className="text-slate-600">
                          Or open Google Authenticator → tap <strong>+</strong> → <strong>Enter a setup key</strong>, then paste:
                        </p>
                        <div className="mt-2 flex items-center gap-2">
                          <code className="flex-1 rounded bg-slate-100 px-2 py-1 font-mono text-slate-900 break-all">{secret}</code>
                          <button
                            type="button"
                            onClick={() => {
                              navigator.clipboard?.writeText(secret).catch(() => null);
                            }}
                            className="rounded-md border border-slate-300 bg-white px-2 py-1 font-medium text-slate-700 hover:bg-slate-50"
                          >
                            Copy
                          </button>
                        </div>
                        <p className="mt-2 text-slate-500">Account name: <strong>Routes Made Simple</strong> · Type: Time-based</p>
                      </>
                    )}
                  </div>
                )}
              </div>
            )}

            <p className="mt-5 text-xs font-semibold text-slate-900">Step 2 — Enter the 6-digit code</p>
            <div className="mt-2 flex flex-wrap items-center gap-2">
              <input
                type="text"
                inputMode="numeric"
                autoComplete="one-time-code"
                pattern="[0-9]{6}"
                maxLength={6}
                value={code}
                onChange={(e) => {
                  setCode(e.target.value.replace(/\D/g, ""));
                  if (error) setError(null);
                }}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && code.length === 6 && !busy) void verify();
                }}
                placeholder="123456"
                autoFocus
                className="w-36 rounded-md border-slate-300 px-3 py-2 text-center text-lg font-mono tracking-widest shadow-sm focus:border-brand-500 focus:ring-brand-500"
              />
              <button
                type="button"
                onClick={verify}
                disabled={busy || code.length !== 6}
                className="inline-flex items-center gap-1.5 rounded-md bg-brand-600 px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-brand-700 disabled:cursor-not-allowed disabled:opacity-50"
              >
                <ShieldCheck className="h-4 w-4" />
                {busy ? "Verifying…" : "Verify and finish"}
              </button>
            </div>
            {error && (
              <p className="mt-3 rounded-md bg-rose-50 px-3 py-2 text-xs font-medium text-rose-800">
                {error}
              </p>
            )}
          </div>
        )}

        {phase === "verify" && !qrCodeSvg && error && (
          // Enroll itself failed — show the error + a retry button.
          <div className="mt-6 rounded-md bg-rose-50 px-3 py-3 text-sm text-rose-800">
            <p className="font-semibold">Couldn't start enrollment</p>
            <p className="mt-1 text-xs">{error}</p>
            <button
              type="button"
              onClick={() => window.location.reload()}
              className="mt-3 rounded-md border border-rose-300 bg-white px-3 py-1 text-xs font-medium text-rose-700 hover:bg-rose-100"
            >
              Try again
            </button>
          </div>
        )}

        <p className="mt-6 text-[11px] text-slate-400">
          Lost your phone later? An admin on your team can reset two-factor for you so you can re-enroll on a new device.
        </p>
      </div>
    </div>
  );
}
